import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { Capacitor } from '@capacitor/core'
import { GoogleAuth } from '@southdevs/capacitor-google-auth'
import { SignInWithApple } from '@capacitor-community/apple-sign-in'
import { authClient } from '@/lib/auth'
import { api, webRedirectBase } from '@/lib/api'
import { getAuthEvents, clearAuthEvents, type AuthEvent } from '@/lib/authDiag'
import { validatePassword } from '@/lib/passwordPolicy'
import { trackEvent } from '@/hooks/useAnalytics'

type Mode = 'signin' | 'signup' | 'reset' | 'magic'

// Mirrors MIN_AGE_YEARS in backend/services/profile_completeness.py, which is
// the authority — this copy exists so the form can say no before a round-trip.
// Under-13 accounts are a COPPA problem, not a preference.
const MIN_AGE_YEARS = 13
// Male/Female only — founder decision 2026-08-04 ("yes, please force Male/Female
// only"), made knowing it raises the App Store Guideline 5.1.1(v) risk: without
// a "prefer not to say" option there's no "we ask and accept a refusal" defence
// for a required sex field. See backend/services/profile_completeness.py for
// the two ways to relax it if a reviewer objects.
const SEX_OPTIONS = [
  { id: 'male', label: 'Male' },
  { id: 'female', label: 'Female' },
] as const

// Captured by useAuth when an OAuth callback / session-exchange fails on
// page load. We surface it on LoginPage so users (and the founder) see
// the actual error message instead of a silent bounce back to login.
//
// SessionStorage (not localStorage) so the diagnostic is per-tab and
// auto-clears when the browser is fully closed — this is debugging
// surface, not persistent state.
const SIGNIN_DIAG_KEY = 'trybeup_signin_diag'

interface SigninDiag {
  /** Source of the failure. */
  source: 'oauth_redirect' | 'session_exchange' | 'native_exchange'
  /** Short error code if available (from GoTrue / Google). */
  code?: string
  /** Human-readable message if available. */
  message?: string
  /** Where in the URL the error was found. */
  url_excerpt?: string
  /** When this happened (ISO timestamp). */
  at: string
}

export default function LoginPage() {
  const navigate = useNavigate()
  // Apple Sign-In is shown on iOS (App Store Guideline 4.8 requires it
  // alongside Google) and on the web (GoTrue redirect-based Apple
  // provider). It is NOT shown on Android: the @capacitor-community/
  // apple-sign-in plugin has no Android implementation, and Android
  // users don't have Apple IDs — the button would appear and then
  // throw "not implemented" on tap. Google Sign-In + email/password
  // cover Android. (2026-06-26, Android launch prep.)
  const isAndroidNative =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
  const showAppleSignIn = !isAndroidNative
  // Google Sign-In is fully wired on iOS and web, but NOT on the native
  // Android build. Comment refreshed 2026-08-04 — the situation changed but
  // the conclusion didn't: the @southdevs/capacitor-google-auth native module
  // IS in the Android project now (builds ≥41, cap-sync fix), so "plugin not
  // implemented" is history on current builds. What's still missing is
  // CONFIG: no Android OAuth client in Google Cloud (needs the Play
  // app-signing SHA-1), no androidClientId/serverClientId in
  // capacitor.config.ts, and the backend audience allowlist
  // (GOOGLE_NATIVE_OAUTH_AUDIENCES) only trusts the iOS client — so signIn()
  // would return no usable idToken and the backend would 401 it. Keep the
  // button hidden until those three exist; email/password covers Android.
  // ANDROID ENABLED 2026-08-05: Android OAuth client created in Google Cloud
  // (package + Play-signing SHA-1), androidClientId baked into
  // capacitor.config.ts (native build ≥45), backend accepts the web-client
  // audience. Devices on native builds 41-44 have the plugin but not the
  // config — their tap fails and the catch below asks them to update.
  const showGoogleSignIn = true
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'signin'
  const [mode, setMode] = useState<Mode>(initialMode)
  const navState = location.state as { message?: string; email?: string } | null
  // Prefill email from ?email= (e.g. an accountability-partner invite link that
  // deep-links to signup) so they sign up with the invited address — that's the
  // key the pending invite is matched on. Falls back to any nav-state email.
  const [email, setEmail] = useState(searchParams.get('email') ?? navState?.email ?? '')
  const [password, setPassword] = useState('')
  // Show/hide toggle for the password field (Android QA v1.1.599,
  // Suggestion #1 — testers couldn't verify what they typed on a phone
  // keyboard). Shared by the password + confirm-password inputs.
  const [showPassword, setShowPassword] = useState(false)
  // Independent of `showPassword`, and that is the point. Both fields used to
  // share one toggle, so the single eye button on the Password field silently
  // put the Confirm field — which has no control of its own to explain it — in
  // clear text too. Reported 2026-08-25 as "it showed my password without me
  // asking" during a password-manager autofill. One control reveals one field.
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  // Sign-up only: a confirm-password field (QA Suggestion #2) so a typo
  // in a hidden password field can't create an account the user can't get
  // back into. Validated against `password` in handleSubmit.
  const [confirmPassword, setConfirmPassword] = useState('')
  // Sign-up only: explicit agreement to Terms + Privacy (QA Suggestion #3).
  // The submit button stays disabled until this is checked.
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  // Sign-up only: first name. FirstSessionGuide does ask for one, and won't let
  // you finish onboarding without it — but the account is created server-side on
  // the first authenticated request, BEFORE the guide has asked anything. Sign
  // up, land in the app, close it, and you're a real, usable, nameless account
  // (founder report 2026-08-04, sanya3@hotmail.com). Because most social
  // surfaces fell back to `first_name or email`, that published the person's
  // EMAIL ADDRESS to strangers in their Trybe. Asked here because this is the
  // one screen every email signup passes through, and it runs before the account
  // exists. Google/Apple always supplied a name in their sign-in data, which is
  // why only the email/password path was ever affected.
  const [firstName, setFirstName] = useState('')
  // The rest of the required set, collected here for the same reason as the
  // first name: this screen runs BEFORE the account exists, so it's the only
  // place a requirement can be a real precondition rather than a prompt someone
  // can walk away from. The server refuses every other endpoint until all four
  // are set (backend/services/profile_completeness.py), so collecting them here
  // is what stops a new user meeting that wall on their first screen.
  const [lastName, setLastName] = useState('')
  const [birthYear, setBirthYear] = useState('')
  const [sex, setSex] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(navState?.message ?? null)
  const [rememberMe, setRememberMe] = useState(true)
  const [formLoading, setFormLoading] = useState(false)
  const [oauthLoading, setOauthLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [showResend, setShowResend] = useState(false)
  // Whether to show a "resend confirmation email" action inside the
  // green info banner. Flipped on right after a fresh signup so the
  // user can immediately re-send if the original email never arrives,
  // instead of having to fail a login first to expose the option.
  const [infoShowResend, setInfoShowResend] = useState(false)
  // Diagnostic banner state. Populated on mount from sessionStorage AND
  // from the URL bar (Google sometimes redirects with `?error=...` if
  // the OAuth call itself was rejected upstream). When non-null, a red
  // banner with the full payload renders above the form so the user
  // sees exactly which step failed.
  const [signinDiag, setSigninDiag] = useState<SigninDiag | null>(null)
  // Rolling auth-event log captured by lib/authDiag. Loaded on mount so
  // the "Why was I signed out?" details panel shows the actual chain of
  // events that led the user back to /login — refresh attempt outcomes,
  // SIGNED_OUT events (and whether they were self-initiated), and the
  // session warmer's actions.
  const [authEvents, setAuthEvents] = useState<AuthEvent[]>([])
  // Storage-state forensics. Captured synchronously on mount so the
  // diagnostic banner can tell the user — and the founder reading the
  // copied details — whether localStorage still has the GoTrue session
  // key, whether IndexedDB does, whether localStorage writes work at
  // all, and how many keys localStorage actually contains. This is
  // exactly the data we need to distinguish "Safari wiped everything"
  // from "the IDB mirror restore failed" from "localStorage writes are
  // throwing in private mode" — the three plausible explanations for a
  // /login arrival with no events logged.
  interface StorageProbe {
    localStorage_session_present: boolean
    localStorage_authlog_present: boolean
    localStorage_key_count: number | 'unreadable'
    /** Names of every key present in localStorage (capped at 20 for safety). */
    localStorage_keys: string[]
    localStorage_write_ok: boolean | 'unknown'
    idb_session_present: boolean | 'unknown'
    idb_open_ok: boolean | 'unknown'
    /** Session-expiry tombstone, if our durable-storage adapter ever observed a session. */
    tombstone_last_seen_at: string | null
    tombstone_expires_at: number | null
    tombstone_source: string | null
    /** How long ago we last saw the session, in seconds. Negative means "tombstone is in the future" (clock skew). */
    tombstone_age_seconds: number | null
    /** When the tombstone'd session was supposed to expire vs now, in seconds. Positive = session was killed early. */
    tombstone_seconds_before_expiry: number | null
    user_agent_excerpt: string
  }
  const [storageProbe, setStorageProbe] = useState<StorageProbe | null>(null)
  // Default expanded: the founder kept missing the previous collapsed
  // grey expander when reproducing the Safari mobile sign-out — by the
  // time they noticed, the next sign-in had wiped the log. Showing the
  // events by default means there's no extra tap to remember before the
  // screenshot.
  const [showAuthEvents, setShowAuthEvents] = useState(true)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const loading = formLoading || oauthLoading

  // Reset sign-up-only fields whenever the mode changes so a
  // confirm-password / terms-agreement from an abandoned sign-up can't
  // leak into (or block) a later sign-in, and the eye toggle resets to
  // hidden. Password itself is intentionally preserved across the
  // signin↔signup toggle (users flip modes mid-thought).
  useEffect(() => {
    setConfirmPassword('')
    setAgreedToTerms(false)
    setShowPassword(false)
    setFirstName('')
    setLastName('')
    setBirthYear('')
    setSex('')
  }, [mode])

  // ── OAuth-failure capture ─────────────────────────────────────────
  // Two sources of failures land us back on /login:
  //
  // 1. Google rejected the OAuth call upstream — the redirect URL
  //    carries `?error=...&error_description=...` or `#error=...`.
  // 2. GoTrue accepted the code but the client-side exchange in
  //    useAuth failed — useAuth writes the diagnostic to sessionStorage
  //    (key SIGNIN_DIAG_KEY) before falling through to unauthenticated.
  //
  // We read both on mount, prefer the URL params (more recent + carry
  // the actual GoTrue / Google error), and clear the storage entry
  // once it's been surfaced so the banner doesn't reappear on later
  // visits to /login.
  useEffect(() => {
    // First, check the URL — most specific source.
    const urlParams = new URLSearchParams(location.search)
    const hashParams = new URLSearchParams(
      window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash,
    )
    const errCode = urlParams.get('error') || hashParams.get('error') || urlParams.get('error_code')
    const errDesc = urlParams.get('error_description') || hashParams.get('error_description')
    if (errCode || errDesc) {
      setSigninDiag({
        source: 'oauth_redirect',
        code: errCode || undefined,
        message: errDesc ? decodeURIComponent(errDesc.replace(/\+/g, ' ')) : undefined,
        url_excerpt: window.location.search + window.location.hash,
        at: new Date().toISOString(),
      })
      // Clean the URL so a refresh doesn't re-show the banner.
      window.history.replaceState({}, '', '/login')
      return
    }

    // Second, fall back to sessionStorage (set by useAuth when the
    // session exchange fails post-redirect).
    try {
      const raw = sessionStorage.getItem(SIGNIN_DIAG_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as SigninDiag
        setSigninDiag(parsed)
        sessionStorage.removeItem(SIGNIN_DIAG_KEY)
      }
    } catch { /* ignore — diagnostic only */ }

    // Always read the rolling auth-event log too — it persists through
    // every sign-out path (proactive warmer, reactive 401, SDK-internal
    // SIGNED_OUT) so even sign-outs that didn't set the SIGNIN_DIAG_KEY
    // show up here. The log stays around until cleared, which only
    // happens after a successful sign-in (handled below).
    setAuthEvents(getAuthEvents())

    // Storage-state probe. Runs in parallel with the IDB read; the
    // synchronous half (localStorage checks) completes immediately, the
    // async half (IDB check) updates the panel once it resolves. Wrapped
    // in defensive try/catch because the entire point of this probe is
    // to diagnose storage failures — it must never become the thing
    // that breaks the login page.
    const probe: StorageProbe = {
      localStorage_session_present: false,
      localStorage_authlog_present: false,
      localStorage_key_count: 'unreadable',
      localStorage_keys: [],
      localStorage_write_ok: 'unknown',
      idb_session_present: 'unknown',
      idb_open_ok: 'unknown',
      tombstone_last_seen_at: null,
      tombstone_expires_at: null,
      tombstone_source: null,
      tombstone_age_seconds: null,
      tombstone_seconds_before_expiry: null,
      user_agent_excerpt:
        typeof navigator !== 'undefined' && navigator.userAgent
          ? navigator.userAgent.slice(0, 120)
          : 'unknown',
    }
    try {
      probe.localStorage_session_present = localStorage.getItem('trybeup_session') !== null
      probe.localStorage_authlog_present = localStorage.getItem('trybeup_auth_log') !== null
      probe.localStorage_key_count = localStorage.length
      const keys: string[] = []
      for (let i = 0; i < localStorage.length && i < 20; i++) {
        const k = localStorage.key(i)
        if (k) keys.push(k)
      }
      probe.localStorage_keys = keys
    } catch { /* localStorage blocked entirely (Safari private mode) */ }
    try {
      const probeKey = '__trybeup_probe__'
      localStorage.setItem(probeKey, '1')
      probe.localStorage_write_ok = localStorage.getItem(probeKey) === '1'
      localStorage.removeItem(probeKey)
    } catch {
      probe.localStorage_write_ok = false
    }
    // Read the session tombstone written by the durable-storage adapter.
    // This is the smoking-gun field for "Safari killed my session early":
    // if the tombstone says expires_at was 50 minutes in the future when
    // the session disappeared, Safari killed it — it didn't expire.
    try {
      const raw = localStorage.getItem('trybeup_session_tombstone')
      if (raw) {
        const t = JSON.parse(raw)
        if (t && typeof t.last_seen_at === 'string') {
          probe.tombstone_last_seen_at = t.last_seen_at
          probe.tombstone_expires_at = typeof t.expires_at === 'number' ? t.expires_at : null
          probe.tombstone_source = typeof t.source === 'string' ? t.source : null
          const lastSeenMs = Date.parse(t.last_seen_at)
          if (Number.isFinite(lastSeenMs)) {
            probe.tombstone_age_seconds = Math.round((Date.now() - lastSeenMs) / 1000)
          }
          if (typeof t.expires_at === 'number') {
            probe.tombstone_seconds_before_expiry = t.expires_at - Math.floor(Date.now() / 1000)
          }
        }
      }
    } catch { /* malformed tombstone — leave nulls */ }
    setStorageProbe(probe)

    // Async IDB check. Uses raw indexedDB rather than the
    // durableSessionStorage adapter so we see the *real* IDB state, not
    // the post-restore localStorage state.
    ;(async () => {
      if (typeof indexedDB === 'undefined') {
        setStorageProbe((prev) => prev && { ...prev, idb_open_ok: false, idb_session_present: false })
        return
      }
      try {
        const db = await new Promise<IDBDatabase | null>((resolve) => {
          let settled = false
          const finish = (v: IDBDatabase | null) => {
            if (settled) return
            settled = true
            resolve(v)
          }
          try {
            const req = indexedDB.open('trybeup_auth', 1)
            req.onerror = () => finish(null)
            req.onblocked = () => finish(null)
            req.onupgradeneeded = () => {
              try {
                if (!req.result.objectStoreNames.contains('kv')) {
                  req.result.createObjectStore('kv')
                }
              } catch { /* ignore — the adapter will retry on next setItem */ }
            }
            req.onsuccess = () => finish(req.result)
            setTimeout(() => finish(null), 2000)
          } catch {
            finish(null)
          }
        })
        if (!db) {
          setStorageProbe((prev) => prev && { ...prev, idb_open_ok: false, idb_session_present: false })
          return
        }
        try {
          const has = await new Promise<boolean>((resolve) => {
            try {
              const tx = db.transaction('kv', 'readonly')
              const store = tx.objectStore('kv')
              const req = store.get('trybeup_session')
              req.onerror = () => resolve(false)
              req.onsuccess = () => resolve(typeof req.result === 'string' && req.result.length > 0)
            } catch {
              resolve(false)
            }
          })
          setStorageProbe((prev) => prev && { ...prev, idb_open_ok: true, idb_session_present: has })
        } finally {
          try { db.close() } catch { /* ignore */ }
        }
      } catch {
        setStorageProbe((prev) => prev && { ...prev, idb_open_ok: false, idb_session_present: false })
      }
    })()
  }, [location.search])

  // Surface the diagnostic panel whenever there's anything diagnostic to
  // surface. Originally we filtered to a narrow set of "suspicious"
  // event types (warmer_signout / reactive_signout / unmarked SIGNED_OUT)
  // but that hid the case where Safari wiped localStorage out from under
  // us — the wipe takes the auth log with it, so the only event we have
  // post-wipe is the benign `initial_session{hasSession:false}` from the
  // fresh page load, which fails every "suspicious" check. With the
  // panel hidden, the founder reproducing the bug saw nothing on /login
  // and couldn't tell me what happened. The trigger is now:
  //
  //   - any "suspicious" signout signature in the log, OR
  //   - the most recent event indicates we loaded /login with no
  //     session (initial_session with hasSession=false), OR
  //   - the storage probe shows localStorage empty / IDB session
  //     present-but-localStorage-absent / writes failing — all of which
  //     are evidence of a Safari storage wipe that we want to capture.
  //
  // The 6-hour cutoff stays so an old debug log doesn't keep haunting
  // the page after a clean Log Out → Sign In cycle (which calls
  // clearAuthEvents()).
  const SUSPICIOUS_WINDOW_MS = 6 * 60 * 60 * 1000
  const hasSuspiciousSignout = useMemo(() => {
    const cutoff = Date.now() - SUSPICIOUS_WINDOW_MS
    const recent = authEvents.filter((ev) => {
      const t = Date.parse(ev.at)
      return Number.isFinite(t) && t >= cutoff
    })
    // Classic suspicious signatures.
    const suspicious = recent.some((ev) => {
      if (ev.type === 'warmer_signout') return true
      if (ev.type === 'reactive_signout') return true
      if (ev.type === 'signed_out_event' && ev.detail.selfInitiated === false) return true
      return false
    })
    if (suspicious) return true
    // Storage anomalies that indicate the user is on /login because of
    // a storage problem, not a first-time visit.
    if (storageProbe) {
      if (storageProbe.localStorage_write_ok === false) return true
      if (
        storageProbe.idb_session_present === true &&
        storageProbe.localStorage_session_present === false
      ) {
        // IDB has a session but localStorage doesn't — the durable
        // adapter should have restored it. If we ended up on /login
        // anyway, something downstream rejected the restored session.
        return true
      }
    }
    // "Landed on /login with no session" but ALSO evidence of a prior
    // session in the same browser. We need the prior-session evidence
    // because `initial_session{hasSession:false}` also fires for a
    // brand-new visitor who's never signed in — we don't want the
    // diagnostic to ambush strangers. Prior-session signals:
    //   - any event other than initial_session (warmer/refresh/signout
    //     events imply a session existed at some point)
    //   - an initial_session with hasSession=true earlier in the log
    //   - any trybeup_* leftover key in localStorage (e.g.
    //     trybeup_onboarding_complete, trybeup_last_active) — survives
    //     a session wipe but proves the user has used the app before
    const initWithNoSession = recent.some(
      (ev) => ev.type === 'initial_session' && ev.detail.hasSession === false,
    )
    if (initWithNoSession) {
      // Prior-session evidence: events that ONLY occur when a session
      // existed (or was being created). `warmer_run` is excluded
      // because it fires on every page load with skipped=true,reason=
      // 'no session or no expires_at' even for brand-new visitors —
      // counting it as prior-session evidence would ambush strangers
      // with the diagnostic panel.
      const priorSessionInLog = authEvents.some((ev) => {
        if (ev.type === 'initial_session') return ev.detail.hasSession === true
        // warmer_run is only meaningful as evidence when it actually ran
        // (skipped=false means there was a live session to potentially refresh).
        if (ev.type === 'warmer_run') return ev.detail.skipped === false
        // Storage adapter events are prior-session evidence when they
        // succeeded for a session value (writes/restores). Failures and
        // removes by themselves aren't.
        if (ev.type === 'durable_session_write') return ev.detail.idb_outcome === 'ok' || ev.detail.local_ok === true
        if (ev.type === 'durable_session_restore') return ev.detail.outcome === 'restored-from-idb'
        // All other event types (refresh_*, warmer_signout, signed_out_*,
        // signout_initiated, reactive_signout, durable_session_remove)
        // only fire when a session existed.
        return true
      })
      if (priorSessionInLog) return true
      // Tombstone is unambiguous prior-session evidence — the durable-
      // storage adapter only writes it after seeing a session value.
      if (storageProbe?.tombstone_last_seen_at) return true
      // Dropped: the "any leftover trybeup_* localStorage key" heuristic.
      // Reproduction showed it triggers in Safari Private mode for a
      // visitor who has never signed in — the leftover key was a stale
      // `trybeup_onboarding_complete` from earlier in the same Private
      // session. Showing the alarming "Why was I signed out?" panel to
      // someone who never had a session is worse than missing a true
      // sign-out (we still catch those via tombstone + event log).
    }
    return false
  }, [authEvents, storageProbe])

  const clearMessages = () => {
    setError(null)
    setInfo(null)
    setShowResend(false)
    setInfoShowResend(false)
    setSigninDiag(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (loading) return
    clearMessages()

    // Signup talks to GoTrue directly, so this check IS the enforcement —
    // there's no server in the call path to catch a weak password afterwards.
    // See lib/passwordPolicy.ts for why that's acceptable.
    if (mode === 'signup') {
      const pwError = validatePassword(password, { email })
      if (pwError) {
        setError(pwError)
        return
      }
    }

    if (mode === 'signup' && password !== confirmPassword) {
      setError('Passwords don\'t match — please re-type them.')
      return
    }

    if (mode === 'signup' && !agreedToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy to continue.')
      return
    }

    // A name is the only thing other people see. Without it the app falls back
    // to the email address on Trybe boards and notifications, so an empty name
    // isn't a cosmetic gap — it publishes the address. `required` on the input
    // covers the normal path; this catches whitespace-only input, which the
    // browser accepts.
    if (mode === 'signup' && !firstName.trim()) {
      setError('Please add your first name — it\'s the name your Trybe will see.')
      return
    }

    // The remaining required fields. The server enforces the same set on every
    // endpoint, so anything missing here just moves the wall one screen later —
    // better to ask once, now, than to hand someone a locked app.
    if (mode === 'signup' && !lastName.trim()) {
      setError('Please add your last name.')
      return
    }

    if (mode === 'signup') {
      const year = Number(birthYear)
      const newestAllowed = new Date().getFullYear() - MIN_AGE_YEARS
      if (!birthYear || !Number.isInteger(year) || year < 1900 || year > newestAllowed) {
        setError(`Please enter your year of birth — you need to be ${MIN_AGE_YEARS} or older to use TrybeUP.`)
        return
      }
    }

    if (mode === 'signup' && !sex) {
      setError('Please choose an option for sex — it sets your calorie and nutrition targets.')
      return
    }

    setFormLoading(true)

    const trimmedEmail = email.trim().toLowerCase()

    try {
      if (mode === 'signin') {
        const { error } = await authClient.signInWithPassword({ email: trimmedEmail, password })
        if (error) throw error
        // Mark session as temporary if "Remember me" is unchecked. useAuth's
        // heartbeat keeps `trybeup_last_active` fresh while the tab is open;
        // if it goes stale past the idle limit, useAuth signs the user out
        // on next load. localStorage (not sessionStorage) so iOS Safari
        // suspending the tab during phone sleep doesn't look like a close.
        //
        // NATIVE EXCEPTION (2026-08-07): the temp-session idle sign-out is a
        // web / shared-computer notion ("log me out when I close the browser").
        // On the native app a phone IS the personal device — you don't "close
        // the browser" — and a 30-minute idle sign-out just logs people out
        // mid-workout (founder: signed out to the logged-out screen with a
        // workout still running). So on native we NEVER mark the session
        // temporary, regardless of the checkbox, and clear any stale flag.
        if (!rememberMe && !Capacitor.isNativePlatform()) {
          localStorage.setItem('trybeup_session_temporary', 'true')
          localStorage.setItem('trybeup_last_active', String(Date.now()))
        } else {
          localStorage.removeItem('trybeup_session_temporary')
          localStorage.removeItem('trybeup_last_active')
        }
        clearAuthEvents()
        navigate('/app', { replace: true })
      } else if (mode === 'signup') {
        // ROLLBACK 2026-05-13: the backend-mediated /api/public/signup path
        // (PR #315, branded confirmation email) was silently dropping
        // confirmation emails on production — `_send_branded_signup_link`
        // returns early without sending when GoTrue's `generate_link` for
        // `type=signup` produces no `action_link`, which appears to be the
        // case on GoTrue v2.159.1 for users created via `admin/users` (the
        // admin API seems to auto-confirm those users regardless of the
        // `email_confirm: false` flag we pass). SendGrid logs showed zero
        // "Confirm your TrybeUP email" entries for fresh signups — the
        // backend never got to send_email. Reverting to GoTrue's native
        // signUp so users get the bland-but-working confirmation email.
        // Branded-email path stays in the backend, unused until we can
        // investigate properly on dev with backend log access.
        const trimmedFirstName = firstName.trim().slice(0, 50)
        const trimmedLastName = lastName.trim().slice(0, 50)
        const { data, error } = await authClient.signUp({
          email: trimmedEmail,
          password,
          // Route the confirmation link to the live web on native (an email
          // client can't open capacitor://localhost) so it never dead-ends.
          options: {
            emailRedirectTo: `${webRedirectBase()}/app`,
            // Becomes GoTrue's `user_metadata`, which rides along in the access
            // token. `backend/dependencies.py` reads exactly these keys when it
            // auto-provisions the TrybeUP user row on the first authenticated
            // request — `given_name` is the one Google supplies, `first_name`
            // and `full_name` are the fallbacks it checks after it. Sending all
            // three means the row is named no matter which key that code
            // prefers, now or later.
            data: {
              first_name: trimmedFirstName,
              given_name: trimmedFirstName,
              family_name: trimmedLastName,
              last_name: trimmedLastName,
              full_name: `${trimmedFirstName} ${trimmedLastName}`.trim(),
              // Not part of GoTrue's OAuth vocabulary — these are ours, read by
              // the same auto-provision code so the account is born complete
              // and never meets the profile gate.
              birth_year: Number(birthYear),
              gender: sex,
            },
          },
        })
        if (error) throw error
        // GoTrue returns a fake user with no identities when the email
        // already exists (enumeration prevention). Detect that.
        if (data.user && data.user.identities && data.user.identities.length === 0) {
          // GoTrue obfuscates an existing account (empty identities) whether it
          // was confirmed OR signed up but never confirmed — we can't tell which.
          // The unconfirmed case is a real trap (Richard, 2026-07-14): the person
          // is stuck — can't sign in (email not confirmed), and re-signing up just
          // lands back here with no way to get a fresh link. Always offer a resend
          // so an unconfirmed account can recover; on a confirmed account the
          // resend simply no-ops. See handleResendConfirmation.
          setInfo('An account with this email may already exist. If you signed up before but never confirmed your email, resend the link below. Otherwise, sign in or use "Forgot password?".')
          setInfoShowResend(true)
          setMode('signin')
        } else {
          // Funnel: top of the acquisition funnel — a genuinely new account was
          // created (pending email confirm). The /analytics/track endpoint is
          // public, so this fires pre-auth. Leaky-buckets D.
          trackEvent('signup_completed', { method: 'password' })
          // On native the confirmation link opens the live web (an email client
          // can't open capacitor://localhost), so it signs the user in in the
          // browser, NOT in the app — promising "signed in automatically" here
          // sets the wrong expectation, so tell app users to come back and sign
          // in (new-user path review 2026-07-26). Web genuinely auto-signs-in.
          setInfo(
            Capacitor.isNativePlatform()
              ? 'Account created! Check your email and tap the confirmation link, then come back here and sign in. The link expires in 24 hours.'
              : 'Account created! Check your email and tap the confirmation link — you\'ll be signed in automatically. The link expires in 24 hours.'
          )
          setInfoShowResend(true)
          setMode('signin')
        }
      } else if (mode === 'reset') {
        await api.post('/api/public/request-password-reset', {
          email: trimmedEmail,
          redirect_to: `${webRedirectBase()}/reset-password`,
        })
        setInfo('Password reset email sent — check your inbox.')
        setMode('signin')
      } else if (mode === 'magic') {
        // Passwordless signin/signup. GoTrue handles both cases via one
        // call — if no account exists for this email, one is created.
        // The user receives a link; clicking it lands them at the
        // emailRedirectTo URL with a session already established (no
        // separate confirmation step). This is the audit's #3 retention
        // fix — eliminates the password creation friction AND collapses
        // signup + email-confirm into a single click.
        const { error } = await authClient.signInWithOtp({
          email: trimmedEmail,
          options: {
            emailRedirectTo: `${webRedirectBase()}/app`,
          },
        })
        if (error) throw error
        setInfo("Magic link sent — check your inbox and tap the link to sign in. It expires in 1 hour.")
        setInfoShowResend(false)
      }
    } catch (err: unknown) {
      let msg = err instanceof Error ? err.message : 'An unexpected error occurred.'
      const lower = msg.toLowerCase()
      // Supabase returns this when the email hasn't been confirmed yet
      if (lower.includes('email not confirmed')) {
        msg = 'Please confirm your email address first — check your inbox for the confirmation link.'
        setShowResend(true)
      } else if (lower.includes('invalid login credentials') || lower.includes('invalid_credentials')) {
        // GoTrue reports an UNCONFIRMED account's sign-in as "invalid
        // credentials" (not "email not confirmed"), so someone who just
        // signed up but hasn't clicked the email link sees a misleading
        // "wrong password". On the sign-in tab, surface the real likely
        // cause and expose Resend (reported 2026-07-14).
        if (mode === 'signin') {
          msg = "Incorrect email or password. If you just created this account, you need to confirm your email first — check your inbox and spam folder, then tap Resend below."
          setShowResend(true)
        } else {
          msg = 'Incorrect email or password — please try again.'
        }
      } else if (lower.includes('rate limit') || lower.includes('too many requests') || lower.includes('request rate')) {
        msg = 'Too many attempts — please wait a moment and try again.'
      }
      setError(msg)
    } finally {
      setFormLoading(false)
    }
  }

  async function handleGoogleSignIn() {
    if (loading) return
    // Android enabled 2026-08-05 — see the showGoogleSignIn comment.
    clearMessages()
    setOauthLoading(true)
    try {
      if (Capacitor.isNativePlatform()) {
        // Native iOS / Android — use the system Google sign-in sheet.
        // No browser hop, no AASA dependency, no PKCE roundtrip.
        //
        // ── Why we go via our backend instead of `signInWithIdToken` ───
        // GoogleSignIn 6.x delegates the OAuth dance to AppAuth, whose
        // convenience initialiser unconditionally injects a random nonce
        // into the auth request (`OIDAuthorizationRequest.m`:
        // `nonce:[[self class] generateState]`). Google echoes that nonce
        // as a claim in the idToken. The @southdevs plugin only surfaces
        // the idToken — it never exposes the request nonce — so we have
        // no way to pass a matching `nonce` to
        // `authClient.signInWithIdToken({ provider, token, nonce })`,
        // and GoTrue rejects the token with: "Passed nonce and nonce in
        // id_token should either both exist or not."
        //
        // The fix is to skip GoTrue's idToken path on native entirely.
        // We POST the raw idToken to our own backend, which:
        //   1. verifies it server-side against Google's public JWKs
        //      (audience = the iOS Client ID),
        //   2. ensures a GoTrue user row exists for that email,
        //   3. mints a real GoTrue-issued session via the admin API and
        //      returns `{access_token, refresh_token}`.
        // We then install that session client-side via `setSession`, and
        // future refresh-token rotations work normally because the
        // refresh_token is a real one GoTrue itself owns.
        //
        // Watchdog: 60s timeout. Was 30s, but first-time cold-start sign-ins
        // on iPad can legitimately exceed 30s during SDK init + token exchange;
        // founder hit a false timeout on first attempt during iPad testing.
        const signInPromise = GoogleAuth.signIn({ scopes: ['profile', 'email'] })
        // clearTimeout whether sign-in resolved OR rejected, so the 60s timer
        // can't fire a late unhandled rejection ~57s after a fast success
        // (Sentry noise). 2026-06-28 auth review.
        let googleTimer: ReturnType<typeof setTimeout> | undefined
        const timeoutPromise = new Promise<never>((_, reject) => {
          googleTimer = setTimeout(() => reject(new Error('Google sign-in timed out — please try again.')), 60000)
        })
        const result = await Promise.race([signInPromise, timeoutPromise])
          .finally(() => { if (googleTimer) clearTimeout(googleTimer) })
        const idToken = result.authentication?.idToken
        if (!idToken) {
          // Android builds 41-44 have the plugin without androidClientId —
          // their sign-in yields no token. The message tells the user the
          // actual fix rather than a dead-end error.
          throw new Error(
            Capacitor.getPlatform() === 'android'
              ? 'Google sign-in needs the latest app version — update TrybeUP in the Play Store and try again.'
              : 'No idToken returned from Google sign-in.',
          )
        }

        // Exchange the idToken for a GoTrue session via our backend.
        // The route is public + strict-rate-limited (`/api/auth/*` bucket
        // in `backend/middleware/rate_limit.py`).
        const { data } = await api.post<{
          access_token: string
          refresh_token: string
          expires_in: number
          user_id: string
        }>('/api/auth/native-google', { id_token: idToken })

        const { error: setSessionError } = await authClient.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        })
        if (setSessionError) throw setSessionError

        // On native there's no redirect-driven PKCE exchange, so we navigate
        // explicitly — but to '/', NOT '/app'. The SIGNED_IN React state can
        // lag the setSession() resolve by a render; in that gap session is
        // still null and isLoading false, so a target of '/app' falls through
        // UnauthenticatedApp's catch-all `<Navigate to="/login">` and flashes
        // the login screen. '/' renders the Gateway when unauthenticated and
        // MainApp once authenticated — no bounce either way. This mirrors the
        // web PKCE path, which navigates to origin '/' for the same reason.
        // 2026-06-28 auth review.
        clearAuthEvents()
        navigate('/', { replace: true })
        setOauthLoading(false)
        return
      }

      // Web — keep the existing redirect-based PKCE flow exactly as-is.
      const { error } = await authClient.signInWithOAuth({
        provider: 'google',
        // Redirect to root so the PKCE ?code= param lands on a route that
        // UnauthenticatedApp renders without a <Navigate> (Landing at "/").
        // If we redirect to "/app", the catch-all "<Navigate to='/login'>" fires
        // during isLoading, stripping the code before GoTrueClient can exchange it.
        //
        // `prompt=select_account` forces Google to always show its account
        // picker — otherwise Google silently auto-signs the user in as the
        // most recently used Google account in Chrome, which prevents
        // switching between multiple Google accounts after a signOut.
        options: {
          redirectTo: window.location.origin,
          queryParams: { prompt: 'select_account' },
        },
      })
      if (error) throw error
      // Browser should redirect shortly; reset loading if it doesn't
      setTimeout(() => setOauthLoading(false), 5000)
    } catch (err: unknown) {
      // GoogleAuth on iOS throws a string "popup_closed_by_user" / "12501" /
      // similar when the user dismisses the sheet — surface a friendlier
      // message in those cases rather than a raw SDK error.
      //
      // For backend axios failures (POST /api/auth/native-google), the raw
      // err.message is generic ("Request failed with status code 502").
      // Pull the actual `detail` field from the response body so the user
      // sees the step-specific reason the backend tagged each 502 with
      // (e.g. "step:verify status:400 body:..."), which turns the iOS
      // sign-in glitch from "blind" to "we know exactly what failed".
      // Same pattern surfaces FastAPI HTTPException(detail=...) responses.
      let msg = err instanceof Error ? err.message : typeof err === 'string' ? err : 'Google sign-in failed.'
      const errAny = err as { response?: { data?: { detail?: unknown; message?: unknown } } }
      const respDetail = errAny?.response?.data?.detail
      const respMessage = errAny?.response?.data?.message
      if (typeof respDetail === 'string' && respDetail.trim()) {
        msg = respDetail
      } else if (typeof respMessage === 'string' && respMessage.trim()) {
        msg = respMessage
      }
      const lower = msg.toLowerCase()
      if (lower.includes('canceled') || lower.includes('cancelled') || lower.includes('popup_closed') || lower.includes('12501')) {
        msg = 'Google sign-in was cancelled.'
      } else if (lower.includes('not implemented')) {
        // Stale native shell: the button ships via OTA to shells of any age,
        // and pre-45 (or dead-manifest 41-46) shells reject the plugin call
        // outright. Without this mapping the user saw the raw
        // '"GoogleAuth" plugin is not implemented on android'. Match ONLY
        // "not implemented" (the plugin-missing phrase) — not "not
        // available", which can appear in a genuine backend 502 excerpt and
        // would then be masked as "update your app" (audit 2026-08-06).
        msg = 'Google sign-in needs the latest app version — update TrybeUP in the app store and try again.'
      }
      setError(msg)
      setOauthLoading(false)
    }
  }

  /**
   * Sign in with Apple — added for App Store compliance (Apple Guideline 4.8).
   * Two code paths, same shape as Google:
   *  - Native iOS: uses the system Sign-in-with-Apple sheet via
   *    @capacitor-community/apple-sign-in. Returns an identityToken that
   *    we POST to /api/auth/native-apple. Backend verifies it against
   *    Apple's public JWKS, ensures a GoTrue user row exists, and mints
   *    a real GoTrue session (mirrors the Google flow).
   *  - Web: redirect-based PKCE via GoTrue's `apple` provider. GoTrue
   *    handles the OAuth dance with the Services ID we registered in
   *    Apple Developer Portal.
   *
   * First-login-only gotcha: Apple returns the user's name/email ONLY on
   * the very first sign-in. Subsequent sign-ins return the identity
   * token alone. The backend captures fullName from the plugin response
   * the first time and persists it; later calls just authenticate.
   */
  async function handleAppleSignIn() {
    if (loading) return
    clearMessages()
    setOauthLoading(true)
    try {
      if (Capacitor.isNativePlatform()) {
        // Native iOS — system Sign-in-with-Apple sheet. We request the
        // user's name + email scopes (only honoured on first sign-in
        // per Apple's design). The plugin's `clientId` is the iOS
        // bundle ID; web uses the separate Services ID via GoTrue.
        // 60s watchdog, mirroring the Google path — a hung Apple sheet or a
        // stuck native call would otherwise strand the user on the spinner
        // forever with no way out but force-quit. clearTimeout on settle so
        // it can't fire late. 2026-06-28 auth review.
        const applePromise = SignInWithApple.authorize({
          clientId: 'com.trybeup.app',
          redirectURI: 'https://auth.trybeup.com/callback',
          scopes: 'email name',
        })
        let appleTimer: ReturnType<typeof setTimeout> | undefined
        const appleTimeout = new Promise<never>((_, reject) => {
          appleTimer = setTimeout(() => reject(new Error('Apple sign-in timed out — please try again.')), 60000)
        })
        const result = await Promise.race([applePromise, appleTimeout])
          .finally(() => { if (appleTimer) clearTimeout(appleTimer) })
        const identityToken = result.response?.identityToken
        if (!identityToken) {
          throw new Error('No identityToken returned from Apple sign-in.')
        }
        const fullName = [
          result.response?.givenName,
          result.response?.familyName,
        ].filter(Boolean).join(' ').trim() || null

        const { data } = await api.post<{
          access_token: string
          refresh_token: string
          expires_in: number
          user_id: string
        }>('/api/auth/native-apple', {
          identity_token: identityToken,
          user: result.response?.user || null,
          full_name: fullName,
          email: result.response?.email || null,
        })

        const { error: setSessionError } = await authClient.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        })
        if (setSessionError) throw setSessionError

        // navigate('/') not '/app' — same race-safe target as the Google
        // native path (see the note there). 2026-06-28 auth review.
        clearAuthEvents()
        navigate('/', { replace: true })
        setOauthLoading(false)
        return
      }

      // Web — redirect-based PKCE via GoTrue's Apple provider.
      const { error } = await authClient.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: window.location.origin,
        },
      })
      if (error) throw error
      setTimeout(() => setOauthLoading(false), 5000)
    } catch (err: unknown) {
      // The Capacitor plugin throws when the user dismisses the sheet —
      // surface a friendly message rather than a raw error code.
      let msg = err instanceof Error ? err.message : typeof err === 'string' ? err : 'Apple sign-in failed.'
      const errAny = err as { response?: { data?: { detail?: unknown; message?: unknown } } }
      const respDetail = errAny?.response?.data?.detail
      const respMessage = errAny?.response?.data?.message
      if (typeof respDetail === 'string' && respDetail.trim()) {
        msg = respDetail
      } else if (typeof respMessage === 'string' && respMessage.trim()) {
        msg = respMessage
      }
      const lower = msg.toLowerCase()
      if (lower.includes('canceled') || lower.includes('cancelled') || lower.includes('user canceled') || lower.includes('1001')) {
        msg = 'Apple sign-in was cancelled.'
      }
      setError(msg)
      setOauthLoading(false)
    }
  }

  async function handleResendConfirmation() {
    if (resendLoading) return
    setResendLoading(true)
    try {
      // ROLLBACK 2026-05-13: matched the signup rollback above — back to
      // GoTrue's native resend so a working bland-but-functional email
      // arrives, instead of our branded path which is silently dropping
      // confirmation emails in production. See the signup rollback note.
      const { error } = await authClient.resend({ type: 'signup', email: email.trim().toLowerCase() })
      if (error) throw error
      setError(null)
      setShowResend(false)
      setInfoShowResend(true)
      setInfo('Confirmation email resent — check your inbox.')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not resend email.'
      setError(msg)
    } finally {
      setResendLoading(false)
    }
  }

  const isReset = mode === 'reset'
  const isSignUp = mode === 'signup'
  const isMagic = mode === 'magic'

  // Live feedback while typing, so nobody discovers our rules by submitting a
  // form three times. Null until they've actually typed something — telling a
  // person their empty password is too short is nagging, not helping.
  const passwordHint = useMemo(
    () => (isSignUp && password.length > 0 ? validatePassword(password, { email }) : null),
    [isSignUp, password, email],
  )

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12">
      {/* Logo / wordmark */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-extrabold tracking-tight text-primary">TrybeUP</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isReset
            ? 'Reset your password'
            : isMagic
            ? 'Sign in with a magic link'
            : isSignUp
            ? 'Create your free account'
            : 'Welcome back'}
        </p>
      </div>

      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="rounded-2xl border border-primary/20 bg-card px-6 py-8 shadow-xl">
          {/* Sign-in diagnostic banner — surfaces the actual reason a
              previous OAuth attempt bounced back to /login. Shown ABOVE
              the error/info banners so the diagnostic isn't covered by
              a fresh attempt's loading state. Auto-clears when the user
              starts typing or taps any action (clearMessages). */}
          {signinDiag && (
            <div
              className="mb-4 rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 text-xs text-amber-700 dark:text-amber-300 space-y-1"
              data-testid="signin-diag-banner"
            >
              <p className="font-semibold text-sm text-amber-700 dark:text-amber-300">
                Sign-in didn't complete
              </p>
              <p className="text-foreground/80 leading-snug">
                {signinDiag.message
                  ? signinDiag.message
                  : signinDiag.code
                    ? `Error code: ${signinDiag.code}`
                    : 'No specific error returned by the sign-in service.'}
              </p>
              {signinDiag.code && signinDiag.message && (
                <p className="text-[11px] text-foreground/60 font-mono">
                  code: {signinDiag.code} · source: {signinDiag.source}
                </p>
              )}
              {!signinDiag.code && (
                <p className="text-[11px] text-foreground/60 font-mono">
                  source: {signinDiag.source}
                </p>
              )}
              {signinDiag.url_excerpt && (
                <p className="text-[10px] text-foreground/50 break-all font-mono leading-relaxed">
                  ↳ {signinDiag.url_excerpt.slice(0, 200)}
                </p>
              )}
              <button
                type="button"
                onClick={() => setSigninDiag(null)}
                className="mt-1 text-[11px] font-medium text-amber-700 dark:text-amber-300 hover:underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Auth-event diagnostic panel — only rendered when the log
              shows a recent SPONTANEOUS sign-out (selfInitiated=false
              within the last 30 mins). User-initiated Log Outs and
              clean visits to /login don't surface it. The underlying
              event logging always runs so the data is still there when
              the Safari ghost-signout reproduces.
              Hidden in production: real users found the amber "Why was
              I signed out?" panel alarming and the diagnostic detail
              unactionable. Logging stays on so we can ask any user who
              reports the issue to paste their authDiag/storage state
              via DevTools. Gate via import.meta.env.DEV so it shows
              only when running `npm run dev` locally. */}
          {import.meta.env.DEV && hasSuspiciousSignout && (
            <div
              className="mb-4 rounded-lg border border-primary/50 bg-primary/10 px-3 py-2 text-xs"
              data-testid="auth-events-panel"
            >
              <button
                type="button"
                onClick={() => setShowAuthEvents((v) => !v)}
                className="w-full text-left font-semibold text-amber-700 dark:text-amber-300"
              >
                {showAuthEvents ? '▼' : '▶'} Why was I signed out? ({authEvents.length} events)
              </button>
              {showAuthEvents && (
                <>
                  <p className="mt-1 text-[11px] text-amber-700/80 dark:text-amber-300/80 leading-snug">
                    If you didn't expect to be signed out, tap <span className="font-semibold">Copy details</span> and send them through — they explain exactly what happened.
                  </p>
                  <div className="mt-2 flex gap-3">
                    <button
                      type="button"
                      onClick={async () => {
                        const eventLines = authEvents
                          .map((ev) => {
                            const kv = Object.entries(ev.detail)
                              .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                              .join(' ')
                            return `${ev.at} ${ev.type} ${kv}`.trim()
                          })
                          .join('\n')
                        const probeLines = storageProbe
                          ? Object.entries(storageProbe)
                              .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                              .join('\n')
                          : 'storageProbe=null'
                        const text = `--- STORAGE STATE ---\n${probeLines}\n\n--- EVENT LOG ---\n${eventLines || '(empty)'}`
                        try {
                          await navigator.clipboard.writeText(text)
                          setCopyState('copied')
                          setTimeout(() => setCopyState('idle'), 2000)
                        } catch {
                          setCopyState('failed')
                          setTimeout(() => setCopyState('idle'), 2000)
                        }
                      }}
                      className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 hover:underline"
                    >
                      {copyState === 'copied' ? '✓ Copied' : copyState === 'failed' ? 'Copy failed — long-press the text below' : 'Copy details'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        clearAuthEvents()
                        setAuthEvents([])
                      }}
                      className="text-[11px] font-medium text-amber-700/70 dark:text-amber-300/70 hover:underline"
                    >
                      Clear log
                    </button>
                  </div>
                  {storageProbe && (
                    <div className="mt-2 rounded bg-background/40 p-2 font-mono text-[10px] text-foreground/70 select-text">
                      <div className="text-foreground/50 mb-1">storage state</div>
                      <div className="break-all leading-snug">
                        {Object.entries(storageProbe).map(([k, v]) => (
                          <span key={k} className="mr-2">
                            <span className="text-foreground/40">{k}=</span>
                            <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-2 space-y-1 font-mono text-[10px] text-foreground/70 max-h-64 overflow-y-auto rounded bg-background/40 p-2 select-text">
                    {authEvents.length === 0 && (
                      <div className="text-foreground/50">
                        event log is empty — likely Safari wiped localStorage along with the log. Storage state above is the diagnostic data we have.
                      </div>
                    )}
                    {authEvents.map((ev, i) => (
                      <div key={i} className="border-t border-foreground/5 pt-1 first:border-t-0 first:pt-0">
                        <div className="text-foreground/50">
                          {ev.at.slice(11, 19)} · <span className="text-foreground/80">{ev.type}</span>
                        </div>
                        <div className="break-all leading-snug">
                          {Object.entries(ev.detail).map(([k, v]) => (
                            <span key={k} className="mr-2">
                              <span className="text-foreground/40">{k}=</span>
                              <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Error / info banners */}
          {error && (
            <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
              {showResend && (
                <button
                  type="button"
                  onClick={handleResendConfirmation}
                  disabled={resendLoading}
                  className="mt-2 block w-full text-center text-xs font-medium text-primary hover:text-primary/80 underline transition disabled:opacity-50"
                >
                  {resendLoading ? 'Sending...' : 'Resend confirmation email'}
                </button>
              )}
            </div>
          )}
          {info && (
            <div className="mb-4 rounded-lg border border-green-500/40 bg-green-500/10 px-4 py-3 text-sm text-green-600">
              {info}
              {infoShowResend && (
                <button
                  type="button"
                  onClick={handleResendConfirmation}
                  disabled={resendLoading || !email.trim()}
                  className="mt-2 block w-full text-center text-xs font-medium text-[#d4a017] hover:text-[#b8860b] underline transition disabled:opacity-50"
                >
                  {resendLoading ? 'Sending...' : "Didn't get the email? Resend"}
                </button>
              )}
            </div>
          )}

          {/* OAuth providers — promoted above email for fastest signup path.
              Apple Sign-In is required by App Store Guideline 4.8 whenever
              Google Sign-In is offered (the two must be equivalent options). */}
          {!isReset && (
            <>
              {showAppleSignIn && (
              <button
                type="button"
                onClick={handleAppleSignIn}
                disabled={loading}
                className="flex w-full items-center justify-center gap-3 rounded-lg border border-foreground/80 bg-foreground py-2.5 text-sm font-medium text-background hover:bg-foreground/90 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="button-apple-signin"
              >
                <svg width="16" height="18" viewBox="0 0 384 512" aria-hidden="true" fill="currentColor">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                </svg>
                {isSignUp ? 'Start with Apple' : 'Continue with Apple'}
              </button>
              )}

              {showGoogleSignIn && (
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="flex w-full items-center justify-center gap-3 rounded-lg border border-primary/25 bg-background py-2.5 text-sm font-medium text-foreground hover:border-primary/50 hover:bg-background/80 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              >
                <svg width="18" height="18" viewBox="0 0 488 512" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 68.7 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
                  />
                </svg>
                {isSignUp ? 'Start with Google' : 'Continue with Google'}
              </button>
              )}

              {/* Terms/Privacy agreement for the non-email sign-up paths. The
                  email+password path has its own explicit checkbox; Apple,
                  Google and magic-link create an account with a single tap, so
                  we present the agreement as implicit-consent copy (the App
                  Store / Play Store expect terms to be presented at account
                  creation on every path, incl. Apple Sign-In which Apple
                  tests). Shown in sign-up mode only. */}
              {isSignUp && (
                <p className="mt-3 text-center text-[11px] leading-snug text-muted-foreground/80">
                  By continuing with Apple, Google, or a magic link, you agree to our{' '}
                  <button type="button" onClick={(e) => { e.preventDefault(); navigate('/terms') }} className="underline hover:text-foreground transition">Terms of Service</button>{' '}
                  and{' '}
                  <button type="button" onClick={(e) => { e.preventDefault(); navigate('/privacy') }} className="underline hover:text-foreground transition">Privacy Policy</button>.
                </p>
              )}

              {/* Divider only makes sense when at least one social button is
                  shown above it. On native Android both Apple and Google are
                  hidden, so suppress the "or use email" divider too. */}
              {(showAppleSignIn || showGoogleSignIn) && (
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-primary/15" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-card px-3 text-xs text-muted-foreground/80">or use email</span>
                </div>
              </div>
              )}
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* First name — sign-up only, and first so it reads as an
                introduction rather than an extra hurdle. This is the only
                place the app has ever asked; before it existed, every
                email/password account arrived with no name and the social
                surfaces printed the person's email address instead. */}
            {isSignUp && (
              <div>
                <label htmlFor="firstName" className="block text-xs font-medium text-foreground/80 mb-1.5">
                  First name
                </label>
                <input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  required
                  maxLength={50}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Sam"
                  className="w-full rounded-lg border border-primary/25 bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition"
                />
                <p className="mt-1 text-xs text-muted-foreground/80">
                  Your Trybe sees your first name only.
                </p>
              </div>
            )}

            {/* Last name */}
            {isSignUp && (
              <div>
                <label htmlFor="lastName" className="block text-xs font-medium text-foreground/80 mb-1.5">
                  Last name
                </label>
                <input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  required
                  maxLength={50}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Hill"
                  className="w-full rounded-lg border border-primary/25 bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition"
                />
              </div>
            )}

            {/* Year of birth. A year rather than a full date: it's all the age
                gate and the calorie maths need, and it's the least personal
                thing that answers both. */}
            {isSignUp && (
              <div>
                <label htmlFor="birthYear" className="block text-xs font-medium text-foreground/80 mb-1.5">
                  Year of birth
                </label>
                <input
                  id="birthYear"
                  type="number"
                  inputMode="numeric"
                  autoComplete="bday-year"
                  required
                  min={1900}
                  max={new Date().getFullYear() - MIN_AGE_YEARS}
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  placeholder="1990"
                  className="w-full rounded-lg border border-primary/25 bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition"
                />
                <p className="mt-1 text-xs text-muted-foreground/80">
                  Sets your calorie and nutrition targets. You must be {MIN_AGE_YEARS} or older.
                </p>
              </div>
            )}

            {/* Sex — required, but "Prefer not to say" is a real answer. */}
            {isSignUp && (
              <div>
                <span className="block text-xs font-medium text-foreground/80 mb-1.5">Sex</span>
                <div className="grid grid-cols-2 gap-2">
                  {SEX_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setSex(option.id)}
                      aria-pressed={sex === option.id}
                      className={`rounded-lg border px-2 py-2.5 text-xs font-medium transition ${
                        sex === option.id
                          ? 'border-primary bg-primary/15 text-primary'
                          : 'border-primary/25 bg-background text-foreground/80 hover:border-primary/50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-muted-foreground/80">
                  Used for your calorie targets — your body burns energy differently.
                </p>
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-foreground/80 mb-1.5">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-lg border border-primary/25 bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition"
              />
            </div>

            {/* Password — hidden in reset and magic-link modes */}
            {!isReset && !isMagic && (
              <div>
                <label htmlFor="password" className="block text-xs font-medium text-foreground/80 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={isSignUp ? 'new-password' : 'current-password'}
                    required
                    minLength={isSignUp ? 8 : undefined}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-lg border border-primary/25 bg-background px-4 py-2.5 pr-11 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground/70 hover:text-foreground transition"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {isSignUp && (
                  passwordHint ? (
                    <p className="mt-1 text-xs text-destructive">{passwordHint}</p>
                  ) : (
                    <p className="mt-1 text-xs text-muted-foreground/80">
                      At least 8 characters — avoid anything guessable
                    </p>
                  )
                )}
              </div>
            )}

            {/* Confirm password — sign-up only. Its OWN show/hide toggle: it
                used to piggyback on the Password field's, which meant one tap
                revealed two fields and this one had no eye of its own to say
                so or to put it back. A mismatch is still easy to spot without
                re-typing — that was the original reason for sharing — because
                the "Passwords don't match" line below does that job without
                anything having to be on screen in clear text. */}
            {isSignUp && (
              <div>
                <label htmlFor="confirmPassword" className="block text-xs font-medium text-foreground/80 mb-1.5">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-lg border border-primary/25 bg-background px-4 py-2.5 pr-11 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label={showConfirmPassword ? 'Hide confirmed password' : 'Show confirmed password'}
                    aria-pressed={showConfirmPassword}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground/70 hover:text-foreground transition"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {confirmPassword.length > 0 && confirmPassword !== password && (
                  <p className="mt-1 text-xs text-destructive">Passwords don't match</p>
                )}
              </div>
            )}

            {/* Terms + Privacy agreement — sign-up only. Submit stays
                disabled until this is checked (App Store / Play Store
                expect an explicit agreement at account creation). */}
            {isSignUp && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 rounded border-primary/25 bg-background text-primary focus:ring-primary focus:ring-offset-0"
                />
                <span className="text-xs text-muted-foreground leading-snug">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); navigate('/terms') }}
                    className="font-medium text-primary hover:underline"
                  >
                    Terms of Service
                  </button>{' '}
                  and{' '}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); navigate('/privacy') }}
                    className="font-medium text-primary hover:underline"
                  >
                    Privacy Policy
                  </button>
                </span>
              </label>
            )}

            {/* Remember me + Forgot password — only in sign-in mode */}
            {mode === 'signin' && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-primary/25 bg-background text-primary focus:ring-primary focus:ring-offset-0"
                  />
                  <span className="text-xs text-muted-foreground">
                    Remember me{' '}
                    <span className="text-muted-foreground/60">(email login only)</span>
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => { clearMessages(); setMode('reset') }}
                  className="text-xs text-primary/70 hover:text-primary transition"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || (isSignUp && !agreedToTerms)}
              className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? 'Please wait…'
                : isReset
                ? 'Send reset email'
                : isMagic
                ? 'Email me a magic link'
                : isSignUp
                ? 'Begin Day 1'
                : 'Sign in'}
            </button>
          </form>

          {/* Google OAuth button moved above the form */}
        </div>

        {/* Toggle sign-in / sign-up / back */}
        <p className="mt-6 text-center text-base text-foreground">
          {isReset ? (
            <>
              Remember it?{' '}
              <button
                type="button"
                onClick={() => { clearMessages(); setMode('signin') }}
                className="font-medium text-primary hover:underline"
              >
                Back to sign in
              </button>
            </>
          ) : isMagic ? (
            <>
              {/* A magic link CREATES an account when none exists
                  (shouldCreateUser defaults true), so consent must be shown
                  here too — before 2026-08-04, signup-via-magic-link from the
                  sign-in tab presented no Terms at all, which both stores
                  check for. */}
              <span className="block mb-2 text-xs text-muted-foreground">
                If you don't have an account yet, the link creates one — by
                continuing you agree to our{' '}
                <a href="/terms" className="underline">Terms of Service</a> and{' '}
                <a href="/privacy" className="underline">Privacy Policy</a>.
              </span>
              Prefer a password?{' '}
              <button
                type="button"
                onClick={() => { clearMessages(); setMode('signin') }}
                className="font-medium text-primary hover:underline"
              >
                Sign in with password
              </button>
            </>
          ) : isSignUp ? (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => { clearMessages(); setMode('signin') }}
                className="font-medium text-primary hover:underline"
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              Ready to start?{' '}
              <button
                type="button"
                onClick={() => { clearMessages(); setMode('signup') }}
                className="font-medium text-primary hover:underline"
              >
                Create an account
              </button>
            </>
          )}
        </p>

        {/* Magic-link entry — passwordless option, recommended for new
            users from social ads where typing a password kills conversion.
            Hidden in reset mode (it's its own flow) and obviously in magic
            mode itself. The audit (#3) called this the single biggest
            email-confirm drop-off fix. */}
        {!isReset && !isMagic && (
          <p className="mt-2 text-center text-sm text-muted-foreground">
            Or{' '}
            <button
              type="button"
              onClick={() => { clearMessages(); setMode('magic') }}
              className="font-medium text-primary hover:underline"
            >
              email me a magic link
            </button>{' '}
            (no password needed)
          </p>
        )}
      </div>
    </div>
  )
}
